/**
 * Universal numeric value formatter for measurement display.
 *
 * Applies a BE-supplied `{ pattern, mode }` descriptor (the `Format` definition
 * shipped on every numeric `Value` — see `measurementData.schema.json`) to a
 * number, producing a display string. Shared across forms/tools so the FE
 * renders a value exactly the way the backend prescribes.
 *
 * Pattern grammar (matches the schema pattern `^[0#]*(\.[0#]*)?$`):
 *   - `0` — a required digit (always shown; pads with a zero when absent)
 *   - `#` — an optional digit (shown only when significant)
 *   - `.` — the (single, optional) decimal separator
 *
 * The count of `0`s left of `.` is the minimum number of integer digits; the
 * count of `0`s right of `.` is the minimum number of fraction digits; the total
 * length of the fraction pattern is the maximum number of fraction digits.
 *
 * @example
 * formatValue(0.999, { pattern: '#.000',   mode: 'ROUND' }); // '.999'
 * formatValue(0.999, { pattern: '0.000',   mode: 'ROUND' }); // '0.999'
 * formatValue(0.999, { pattern: '000.000', mode: 'ROUND' }); // '000.999'
 * formatValue(0.999, { pattern: '#0.000',  mode: 'ROUND' }); // '0.999'
 */

import type {
  FormatMode,
  ValueFormat,
} from '@shared/types/valueFormat';

export type {
  FormatMode,
  ValueFormat,
} from '@shared/types/valueFormat';

const FORMAT_PATTERN = /^[0#]*(\.[0#]*)?$/;

/** Stopgap default for a numeric `Value` with no BE-supplied `format`. */
export const DEFAULT_VALUE_FORMAT: ValueFormat = { pattern: '0.##', mode: 'ROUND' };

const FORMAT_MODES = ['ROUND', 'FLOOR', 'CEIL'];

const countZeros = (s: string): number => (s.match(/0/g) ?? []).length;

/**
 * Resolve a `ValueFormat` from a measurement config's loosely-typed `metadata`
 * bag (`ClinicalTrialInventoryMeasurementType.metadata`), validated against the
 * same BE `Format` constraints `formatValue` itself parses (mode enum + pattern
 * grammar). A missing or malformed `metadata.format` falls back to `fallback`
 * rather than forwarding something `formatValue` would silently reject.
 */
export const resolveValueFormat = (
  metadata?: Record<string, unknown>,
  fallback: ValueFormat = DEFAULT_VALUE_FORMAT
): ValueFormat => {
  const f = metadata?.format as ValueFormat | undefined;

  const valid =
    !!f &&
    typeof f.pattern === 'string' &&
    FORMAT_PATTERN.test(f.pattern) &&
    typeof f.mode === 'string' &&
    FORMAT_MODES.includes(f.mode);

  return valid ? f : fallback;
};

/**
 * Round `value` to `decimals` fraction digits using the given mode. ROUND is
 * half-up (toward +∞); FLOOR is toward −∞; CEIL is toward +∞. A scaled-then-
 * cleaned step removes binary-float noise so e.g. FLOOR of 0.999 at 3 dp stays
 * 0.999 instead of collapsing to 0.998.
 */
const roundToScaledInt = (value: number, decimals: number, mode: FormatMode): number => {
  const factor = 10 ** decimals;

  // toPrecision(12) strips representation error (998.9999999999 → 999) before
  // the directional rounding that FLOOR/CEIL would otherwise amplify.
  const scaled = Number((value * factor).toPrecision(12));

  if (mode === 'FLOOR') {
    return Math.floor(scaled);
  }
  if (mode === 'CEIL') {
    return Math.ceil(scaled);
  }
  return Math.round(scaled);
};

/**
 * Format a finite number with a BE `{ pattern, mode }` descriptor.
 *
 * Returns '' for a non-finite value. An invalid pattern falls back to the
 * plain string form of the value (the pattern is BE-validated, so this guards
 * only against drift rather than masking a real bug at render time).
 */
export const formatValue = (value: number, format: ValueFormat): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const { pattern, mode } = format;

  if (!FORMAT_PATTERN.test(pattern)) {
    return String(value);
  }

  const dotIndex = pattern.indexOf('.');
  const intPattern = dotIndex === -1 ? pattern : pattern.slice(0, dotIndex);
  const fracPattern = dotIndex === -1 ? '' : pattern.slice(dotIndex + 1);

  const minIntDigits = countZeros(intPattern);
  const minFracDigits = countZeros(fracPattern);
  const maxFracDigits = fracPattern.length;

  const scaledInt = roundToScaledInt(value, maxFracDigits, mode);
  const negative = scaledInt < 0;

  // Split the absolute scaled integer into its integer and fraction digit runs.
  const absDigits = Math.abs(scaledInt)
    .toString()
    .padStart(maxFracDigits + 1, '0');
  const splitAt = absDigits.length - maxFracDigits;
  let intDigits = absDigits.slice(0, splitAt);
  let fracDigits = maxFracDigits > 0 ? absDigits.slice(splitAt) : '';

  // Integer part: drop leading zeros, then pad up to the required minimum.
  intDigits = intDigits.replace(/^0+/, '').padStart(minIntDigits, '0');

  // Fraction part: drop trailing zeros that fall in the optional (`#`) zone.
  while (fracDigits.length > minFracDigits && fracDigits.endsWith('0')) {
    fracDigits = fracDigits.slice(0, -1);
  }

  let result = fracDigits.length > 0 ? `${intDigits}.${fracDigits}` : intDigits;

  if (result === '' || result === '.') {
    result = '0';
  }
  if (negative && scaledInt !== 0) {
    result = `-${result}`;
  }

  return result;
};
